/*
 * Colourway swatches — ported from the Silver Raven theme, which took the approach from st95.
 *
 * Every colourway is a separate product, so a variant picker cannot show them. What the products share is the
 * style name their titles start with, so this product's title is reduced to that (see baseTitle) and the
 * predictive search endpoint is asked for products whose titles start with it.
 *
 * It asks for a rendered section (sections/colour-swatch-results.liquid) rather than JSON, so the swatches pass
 * through Liquid and can read the swatch metafields. This file builds no markup; it fetches it and puts it in
 * place. Results are cached in sessionStorage, since a shopper clicking between colourways would otherwise
 * repeat the same search on every page.
 *
 * Replaces the Left Hand script that opened theme.js, which built swatches from featured_image.url and threw on
 * the first colourway without a photograph, leaving the row empty.
 *
 * Shopify's predictive search returns at most 10 products, so a style with more colourways than that shows ten.
 * Fill in custom.swatch for those — the block renders that list instead and never calls this.
 */

(function () {
  const CACHE_PREFIX = 'colourSwatches:';
  const CACHE_TTL = 30 * 60 * 1000;
  const SEARCH_LIMIT = 10;
  const SECTION_ID = 'colour-swatch-results';

  /*
   * Shopify stamps a version onto every asset URL and changes it on deploy, so this file's own URL dates the
   * markup it produces. Putting that version in the cache key and the request means a deploy or a template
   * change is not hidden behind half an hour of cached swatches.
   */
  function themeVersion() {
    const script = document.querySelector('script[src*="colour-swatch-links.js"]');
    if (!script) return '';

    try {
      return new URL(script.src, window.location.origin).searchParams.get('v') || '';
    } catch (error) {
      return '';
    }
  }

  const VERSION = themeVersion();

  function cacheKey(base) {
    return CACHE_PREFIX + VERSION + ':' + base;
  }

  function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  const SEPARATOR = '[\\s\\-–—,/|]';

  // Reduce a title to the style name every colourway shares. The colour may be appended, hyphenated, or gone
  // from the title altogether, so "Cap Navy", "Cap - Navy", "Cap -" and "Cap" all land on "Cap".
  function baseTitle(title, colours) {
    const usable = (colours || []).filter(Boolean).map(escapeRegExp);
    let base = String(title || '').trim();

    if (usable.length > 0) {
      base = base.replace(new RegExp(SEPARATOR + '*(' + usable.join('|') + ')$', 'i'), '');
    }

    return base.replace(new RegExp(SEPARATOR + '+$'), '').trim();
  }

  // Null for nothing cached, which a search that found nothing is not.
  function readCache(key) {
    try {
      const raw = sessionStorage.getItem(cacheKey(key));
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.timestamp > CACHE_TTL) {
        sessionStorage.removeItem(cacheKey(key));
        return null;
      }
      return parsed.markup;
    } catch (error) {
      return null;
    }
  }

  function writeCache(key, markup) {
    try {
      sessionStorage.setItem(cacheKey(key), JSON.stringify({ timestamp: Date.now(), markup: markup }));
    } catch (error) {
      // Private browsing or a full quota. Not worth failing over.
    }
  }

  function searchUrl() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'search/suggest';
  }

  async function fetchSwatches(base) {
    const cached = readCache(base);
    if (cached !== null) return cached;

    const params = new URLSearchParams({
      q: base,
      'resources[type]': 'product',
      'resources[limit]': String(SEARCH_LIMIT),
      'resources[options][unavailable_products]': 'last',
      section_id: SECTION_ID,
    });

    if (VERSION) params.set('theme_version', VERSION);

    const response = await fetch(searchUrl() + '?' + params.toString());
    if (!response.ok) throw new Error('Search failed: ' + response.status);

    const section = new DOMParser()
      .parseFromString(await response.text(), 'text/html')
      .querySelector('#shopify-section-' + SECTION_ID);
    const markup = section ? section.innerHTML.trim() : '';

    writeCache(base, markup);
    return markup;
  }

  function currentPath() {
    return window.location.pathname.replace(/\/$/, '');
  }

  // The section renders the same markup whichever colourway asked for it, so the one being viewed is marked
  // here rather than there.
  function markCurrent(list) {
    list.querySelectorAll('.swatch-link').forEach(function (link) {
      const path = new URL(link.getAttribute('href'), window.location.origin).pathname.replace(/\/$/, '');
      if (path !== currentPath()) return;

      link.classList.add('active');
      link.setAttribute('aria-current', 'page');
    });
  }

  async function render(container) {
    const dataEl = container.querySelector('[data-colour-swatches-product]');
    const list = container.querySelector('[data-colour-swatches-list]');
    if (!dataEl || !list || list.dataset.colourSwatchesRendered) return;

    let product;
    try {
      product = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }

    const base = baseTitle(product.title, product.colours);
    // A title that is nothing but the colour gives no prefix to search on.
    if (!base) return;

    let markup;
    try {
      markup = await fetchSwatches(base);
    } catch (error) {
      return;
    }

    list.dataset.colourSwatchesRendered = 'true';
    if (!markup) return;

    list.innerHTML = markup;

    // One swatch is this product on its own; a lone swatch tells you nothing.
    if (list.querySelectorAll('.swatch-link').length < 2) {
      list.innerHTML = '';
      return;
    }

    markCurrent(list);
  }

  function init() {
    document.querySelectorAll('[data-colour-swatches]').forEach(render);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Prestige re-renders the product info when a variant changes, which replaces the list with an empty one.
  document.addEventListener('variant:change', function () {
    window.setTimeout(init, 0);
  });
})();
